// SIL-1182: shared "add asset" method model + per-user, in-browser
// last-used-method memory. Individual orgs land on TrueRead by default and
// switch to whatever method they last completed a capture with; business
// orgs are untouched (no memory, always Manual-first — see submit.tsx).
//
// Kept out of routes/assets/submit.tsx so SIL-1183's wizard stepper can
// import the same type + read/write helpers without reaching into route
// internals.
export type AddMethod = 'manual' | 'scan' | 'csv' | 'integration' | 'probe';

const VALID_METHODS: readonly AddMethod[] = ['manual', 'scan', 'csv', 'integration', 'probe'];

function isAddMethod(v: unknown): v is AddMethod {
  return typeof v === 'string' && (VALID_METHODS as readonly string[]).includes(v);
}

/** A brand-new individual-org user (no stored memory yet) lands here. */
export const INDIVIDUAL_DEFAULT_METHOD: AddMethod = 'probe';

const STORAGE_PREFIX = 'siliconx-add-asset-method:';

/**
 * Per-user, in-browser. `userKey` should be something stable per signed-in
 * user (email today — there's no separate customer-portal user id exposed
 * to the browser). A `null`/`undefined` key (identity not loaded yet, or no
 * email on the account) still gets a scoped bucket rather than colliding
 * with every other unresolved caller.
 */
export function addAssetMethodStorageKey(userKey: string | null | undefined): string {
  return `${STORAGE_PREFIX}${userKey ?? 'anon'}`;
}

/**
 * Garbage or missing -> null; caller falls back to `INDIVIDUAL_DEFAULT_METHOD`.
 * Blocked storage (private browsing, quota, disabled) never throws past this.
 */
export function readLastUsedAddMethod(userKey: string | null | undefined): AddMethod | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(addAssetMethodStorageKey(userKey));
    return isAddMethod(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Call only from a successful-capture path — never on mere card selection. */
export function writeLastUsedAddMethod(userKey: string | null | undefined, method: AddMethod): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(addAssetMethodStorageKey(userKey), method);
  } catch {
    // Blocked storage — remembering the method is a nicety, not a
    // requirement; never let it break the page.
  }
}
